/*eslint max-lines-per-function: ["error", 355]*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { TabsHistory } from '../../../app/components/TabsHistory';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { SendFVSByEmailSearch } from '../../components/SendFVSByEmailSearch/SendFVSByEmailSearch';
import ValidationAlert from '../../../app/components/ValidationAlert';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';
import { AlreadySentEmailsSearch } from '../../components/SendFVSByEmailSearch/AlreadySentEmailsSearch';
import { WaitingModal } from '../../../common/components/Waiting/WaitingModal';
import { ClientSearch } from '../../components/Clients/ClientSearch';
import { useNavigation } from '../../../common/navigation';
// import { SendFVSInOneEmailForm } from '../../components/SendFVSByEmailSearch/SendFVSInOneEmailForm';
// import Button from '../../../common/components/Button/Button';

export const SendFVSByEmailView: React.FC = () => {
  const pok = usePokCore();
  const nav = useNavigation();
  const { t, tk } = useTranslation('fvsLng');
  const { t: tClient, tk: tkClient } = useTranslation('clientLng');
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  // const [showModalInOneMail, setShowModalInOneMail] = useState<boolean>(false);

  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [buttonTitleSendByOne, setButtonTitleSendByOne] = useState<string>(
    t(tk.sendEmail.sendAllUnsent),
  );
  const [buttonTitleSendManyInOneMail, setButtonTitleSendManyInOneMail] =
    useState<string>(t(tk.sendEmail.sendAllUnsentInOneEmail));
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    setButtonTitleSendByOne(t(tk.sendEmail.sendAllUnsent));
    setButtonTitleSendManyInOneMail(t(tk.sendEmail.sendAllUnsentInOneEmail));
    setSelectedInvoices([]);
  }, [t, tk.sendEmail.sendAllUnsent, tk.sendEmail.sendAllUnsentInOneEmail]);

  useEffect(() => {
    setButtonTitleSendByOne(
      selectedInvoices.length > 0
        ? `${t(tk.sendEmail.sendUnsent, { number: selectedInvoices.length })}`
        : `${t(tk.sendEmail.sendAllUnsent)}`,
    );
    setButtonTitleSendManyInOneMail(
      selectedInvoices.length > 0
        ? `${t(tk.sendEmail.sendUnsentInOneEmail, { number: selectedInvoices.length })}`
        : `${t(tk.sendEmail.sendAllUnsentInOneEmail)}`,
    );
  }, [selectedInvoices, t, tk.sendEmail]);

  const refreshData = useCallback(() => {
    setRefresh(prev => !prev);
  }, []);

  const createGetWaitingToSend = useCallback(
    (
      withEmailAddress: boolean,
      withOneEmailAddress: boolean,
    ): GridGetterFunction =>
      (
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.salesInvoices.getWaitingToSendByEmailByPortion(
          t,
          tk,
          refresh,
          setRefresh,
          withEmailAddress,
          withOneEmailAddress,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        ),
    [pok.salesInvoices, t, tk, refresh, setRefresh],
  );

  const getDataPackagesWithSentEmails = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.salesInvoices.getWithSentEmailByPortion(
          refreshData,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.salesInvoices, refreshData],
  );

  const getDataPackagesWithEmailAddress = useMemo(
    () => createGetWaitingToSend(true, true),
    [createGetWaitingToSend],
  );

  const getDataPackagesWithoutEmailAddress = useMemo(
    () => createGetWaitingToSend(false, false),
    [createGetWaitingToSend],
  );

  const getDataPackagesWithMoreEmailAddress = useMemo(
    () => createGetWaitingToSend(true, false),
    [createGetWaitingToSend],
  );

  const createGetWaitingToSendCount = useCallback(
    (
      withEmailAddress: boolean,
      withOneEmailAddress: boolean,
    ): GridCountFunction =>
      (filterText, filterColumns) =>
        pok.salesInvoices.getWaitingToSendByEmailCount(
          withEmailAddress,
          withOneEmailAddress,
          filterText,
          filterColumns,
        ),
    [pok.salesInvoices],
  );

  const getCountPackagesWithEmailAddress = useMemo(
    () => createGetWaitingToSendCount(true, true),
    [createGetWaitingToSendCount],
  );

  const getCountPackagesWithoutEmailAddress = useMemo(
    () => createGetWaitingToSendCount(false, false),
    [createGetWaitingToSendCount],
  );

  const getCountPackagesWithMoreEmailAddress = useMemo(
    () => createGetWaitingToSendCount(true, false),
    [createGetWaitingToSendCount],
  );

  const handleOnRowClick = (key: string) => {
    let si: string[];

    if (!selectedInvoices.includes(key)) {
      si = [...selectedInvoices, key];
    } else {
      si = selectedInvoices.filter(o => o !== key);
    }

    setSelectedInvoices(si);
  };

  const sendFVSByMail = async () => {
    try {
      setWaiting(true);
      await pok.salesInvoices.sendFVSByEmails({
        companyId: pok.getCompanyId(),
        selectedInvoices,
      });
      setWaiting(false);
      setShowSaveErrors(false);
      setRefresh(!refresh);
      setSelectedInvoices([]);
      setButtonTitleSendByOne(t(tk.sendEmail.sendAllUnsent));
      setButtonTitleSendManyInOneMail(t(tk.sendEmail.sendAllUnsentInOneEmail));

      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      setWaiting(false);
      setShowSaveErrors(true);
      setSelectedInvoices([]);
      setSaveErrors(await responseErrors(response as ResponseError));
    }
  };

  const sendFVSByMailInOneMail = async (subject?: string) => {
    try {
      setWaiting(true);
      await pok.salesInvoices.sendMultipleFVSInOneEmail({
        companyId: pok.getCompanyId(),
        selectedInvoices,
        emailSubject: subject,
      });
      // setShowModalInOneMail(false);
      setWaiting(false);
      setShowSaveErrors(false);
      setRefresh(!refresh);
      setSelectedInvoices([]);
      setButtonTitleSendByOne(t(tk.sendEmail.sendAllUnsent));
      setButtonTitleSendManyInOneMail(t(tk.sendEmail.sendAllUnsentInOneEmail));

      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      setWaiting(false);
      setShowSaveErrors(true);
      setSelectedInvoices([]);
      setSaveErrors(await responseErrors(response as ResponseError));
    }
  };

  const handleClick = async (id: string, newWindow?: boolean) => {
    nav.purchaserEmailsEditor(id, newWindow);
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.clients.getAllWithFVSEmailByPortion(
          tClient,
          tkClient,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.clients, tClient, tkClient],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.sendEmail.toClients)}</Card.Title>
        <TabsHistory
          activeKey="waitingToSendWithEmail"
          id="waitingToSend"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="waitingToSendWithEmail"
            title={t(tk.sendEmail.waitingToSendWithEmail)}
            className="m-3"
          >
            <ConfirmationButton
              variant="outline-primary"
              className="mb-4 me-1"
              onOK={sendFVSByMail}
              confirmation={t(tk.sendEmail.askSendEmails)}
              isLoading={waiting}
            >
              {buttonTitleSendByOne}
            </ConfirmationButton>
            <ConfirmationButton
              variant="outline-primary"
              className="mb-4 me-1"
              onOK={sendFVSByMailInOneMail}
              confirmation={t(tk.sendEmail.askSendEmailsManyInOneMail)}
              isLoading={waiting}
            >
              {buttonTitleSendManyInOneMail}
            </ConfirmationButton>
            {/* <Button
              variant="outline-primary"
              className="mb-4 me-1"
              onClick={() => {
                setShowModalInOneMail(true);
              }}
              isLoading={waiting}
            >
              {buttonTitleSendManyInOneMail}
            </Button>
            {showModalInOneMail && (
              <SendFVSInOneEmailForm
                selectedInvoices={selectedInvoices}
                show={showModalInOneMail}
                refresh={refresh}
                onSend={sendFVSByMailInOneMail}
                onClose={() => {
                  setShowModalInOneMail(false);
                }}
              />
            )} */}
            <ValidationAlert
              show={showSaveErrors}
              errors={saveErrors}
              className="m-3"
            />
            {waiting && (
              <WaitingModal message={t(tk.sendEmail.sendingInProgress)} />
            )}
            <SendFVSByEmailSearch
              getData={getDataPackagesWithEmailAddress}
              getCount={getCountPackagesWithEmailAddress}
              onRowClick={handleOnRowClick}
              refresh={refresh}
              enableMultiSelect={true}
              selectedIds={selectedInvoices}
            />
          </Tab>
          <Tab
            eventKey="waitingToSendWithMoreEmail"
            title={t(tk.sendEmail.waitingToSendWithMoreEmail)}
            className="m-3"
          >
            <SendFVSByEmailSearch
              getData={getDataPackagesWithMoreEmailAddress}
              getCount={getCountPackagesWithMoreEmailAddress}
              withoutFrom={true}
            />
          </Tab>
          <Tab
            eventKey="waitingToSendWithoutEmail"
            title={t(tk.sendEmail.waitingToSendWithoutEmail)}
            className="m-3"
          >
            <SendFVSByEmailSearch
              getData={getDataPackagesWithoutEmailAddress}
              getCount={getCountPackagesWithoutEmailAddress}
              withAddEmailAddresses={true}
              withoutToCC={true}
              withoutFrom={true}
            />
          </Tab>
          <Tab
            eventKey="sentEmails"
            title={t(tk.sendEmail.sentEmails)}
            className="m-3"
          >
            <AlreadySentEmailsSearch
              getData={getDataPackagesWithSentEmails}
              getCount={pok.salesInvoices.getWithSentEmailsCount}
              refresh={refresh}
            />
          </Tab>
          <Tab
            eventKey="clients"
            title={t(tk.sendEmail.clientsEmails)}
            className="m-3"
          >
            <ClientSearch
              getData={getData}
              getCount={pok.clients.getAllWithFVSEmailCount}
              onRowClick={handleClick}
              refresh={refresh}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
